import { useContext, useMemo } from 'react';

import { ToastContext } from '@/components/common/CommonToast';
import { CustomDataTable } from '@/components/common/ui/table/CustomDataTable';
import { useTranslation } from '@/config/i18n';
import useFetchClient from '@/hooks/useFetchClient';
import usePagination from '@/hooks/usePagination';
import useSearch from '@/hooks/useSearch';
import { normalizeTimeZone, toastErrorMessage } from '@/lib/utils';
import { PaymentColumns } from '@/pages/payment/table/PaymentColumns';
import { apiRequest } from '@/services';
import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from '@/shared/constants/pagination';
import { Pagination } from '@/types/pagination';

type Props = {
  searchTrigger: number;
};

const PaymentList: React.FC<Props> = ({ searchTrigger }) => {
  const [t] = useTranslation('');
  const { apiService } = apiRequest();
  const { searchParams } = useSearch();
  const { toast } = useContext(ToastContext);
  const { query } = Object.fromEntries(searchParams.entries());

  const queryCustom = useMemo(() => {
    return query
      ? `${query}&timezone=${normalizeTimeZone(
        Intl.DateTimeFormat().resolvedOptions().timeZone,
      )}`
      : '';
  }, [query]);

  const { data: payments, error } = useFetchClient(
    async () => await apiService.payments.query(queryCustom, params),
    [query, searchTrigger],
    {
      isLoadingGlobal: true,
      isScrollToTop: true,
    },
  );

  const params = useMemo(() => {
    return query
      ? undefined
      : { limit: DEFAULT_PER_PAGE, paginate: DEFAULT_PAGE };
  }, [query]);

  if (error) {
    toast?.current?.show(toastErrorMessage(t('common.toast.fail')));
  }

  const { paginationComputed, setPagination } = usePagination(
    payments?.pagination,
  );

  function onPaginationChanged(paginationState: Pagination) {
    setPagination({
      ...paginationComputed,
      paginate: paginationState.paginate,
      limit: paginationState.limit,
    });
  }

  function onSortChanged(paginationState: Pagination) {
    setPagination(paginationState);
  }

  return (
    <>
      <CustomDataTable
        wrapClassName="mt-[20px]"
        values={payments?.data || []}
        columns={PaymentColumns(t, paginationComputed)}
        pagination={paginationComputed}
        onPaginationChanged={onPaginationChanged}
        onSortChanged={onSortChanged}
      />
    </>
  );
};

export default PaymentList;
